import { App, Form } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { CustomModal } from "sipli-fleet-core/components";
import { useMutationUpdateWarehouseUnit } from "src/queries/warehouseUnits.queries";
import type { WarehouseUnitRecord } from "src/services/warehouseUnitService";
import PositionFields from "./PositionFields";
import type { UnitFormValues } from "./unitFormShared";

interface EditUnitPositionModalProps {
  open: boolean;
  record: WarehouseUnitRecord | null;
  onClose: () => void;
  onUpdated?: () => void;
}

const POSITION_FIELDS: (keyof UnitFormValues)[] = [
  "warehouseId",
  "areaId",
  "laneId",
  "shelfId",
  "positionId",
];

const EditUnitPositionModal = ({ open, record, onClose, onUpdated }: EditUnitPositionModalProps) => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const [form] = Form.useForm<UnitFormValues>();
  const { mutateAsync: updateUnit, isPending: isUpdating } = useMutationUpdateWarehouseUnit();

  useEffect(() => {
    if (!open) {
      form.resetFields();
      return;
    }

    if (!record) return;

    form.setFieldsValue({
      warehouseId: record.warehouse?.id,
      areaId: record.area?.id,
      laneId: record.lane?.id,
      shelfId: record.shelf?.id,
      positionId: record.position?.id,
    });
  }, [open, record, form]);

  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  const handleSave = async () => {
    if (!record) return;

    try {
      await form.validateFields(POSITION_FIELDS);
      const values = form.getFieldsValue(true);

      const payload = {
        warehouseId: values.warehouseId as string | number,
        positionId: values.positionId,
      };

      await updateUnit({ id: record.id, payload });
      message.success(t("unit_updated_successfully"));
      onUpdated?.();
      handleClose();
    } catch (error) {
      if (error instanceof Error) {
        message.error(t("failed_to_update_unit"));
      }
    }
  };

  return (
    <CustomModal
      open={open}
      onCancel={handleClose}
      okText={t("save")}
      cancelText={t("cancel")}
      onOk={handleSave}
      okBtnLoading={isUpdating}
      width={835}
      destroyOnHidden
      title={t("edit_unit_position")}
    >
      <Form form={form} layout="vertical" requiredMark={false} className="all-units-form">
        <PositionFields form={form} open={open} />
      </Form>
    </CustomModal>
  );
};

export default EditUnitPositionModal;
